import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import { CPE } from '../types/cpe';

interface SearchResult {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
  isPP?: boolean; // Flag to indicate this is a PP result
  ppData?: CPE; // Original PP data if this is a PP result
}

interface MapSearchProps {
  onLocationSelect: (lat: number, lng: number, name: string) => void;
  onPPSelect?: (ppId: string, lat: number, lng: number) => void; // Callback when PP is selected
  placeholder?: string;
  cpes?: CPE[]; // Optional list of CPEs to search through
}

export const MapSearch: React.FC<MapSearchProps> = ({ onLocationSelect, onPPSelect, placeholder, cpes = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpenRef = useRef(false);

  // Update ref whenever expanded or showResults changes
  useEffect(() => {
    isOpenRef.current = expanded || showResults;
  }, [expanded, showResults]);

  // Close search when clicking outside - use ref to avoid stale closures
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if search is currently open using ref (not state)
      if (isOpenRef.current && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Prevent this click from doing anything else
        event.stopPropagation();
        event.preventDefault();
        setShowResults(false);
        setExpanded(false);
      }
    };

    // Use capture phase to intercept before other handlers (map, buttons, etc.)
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []); // Empty deps - listener stays constant, uses ref for current state

  // Focus input when expanded
  useEffect(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded]);

  // Memoized search function
  const performSearch = useCallback(async (query: string) => {
    setLoading(true);
    try {
      // Search in PP names first (local search)
      const ppResults: SearchResult[] = cpes
        .filter(cpe =>
          cpe.name.toLowerCase().includes(query.toLowerCase()) ||
          (cpe.company_cui && cpe.company_cui.toLowerCase().includes(query.toLowerCase()))
        )
        .slice(0, 5) // Limit to 5 PP results
        .map(cpe => ({
          place_id: `pp-${cpe.id}`,
          display_name: `${cpe.name} ${cpe.company_cui ? `(${cpe.company_cui})` : ''}`,
          lat: cpe.latitude.toString(),
          lon: cpe.longitude.toString(),
          type: 'power_plant',
          importance: 1, // High importance for direct PP matches
          isPP: true,
          ppData: cpe,
        }));

      // Use Nominatim API for OpenStreetMap geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=5&` +
        `featuretype=city&` +
        `accept-language=en`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const cityResults: SearchResult[] = await response.json();

      // If no city results, do a general search
      let locationResults: SearchResult[] = [];
      if (cityResults.length === 0) {
        const generalResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(query)}&` +
          `format=json&` +
          `addressdetails=1&` +
          `limit=5&` +
          `accept-language=en`
        );

        locationResults = await generalResponse.json();
      } else {
        locationResults = cityResults;
      }

      // Combine PP results first, then location results
      const combinedResults = [...ppResults, ...locationResults];
      setSearchResults(combinedResults);
      setShowResults(combinedResults.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [cpes]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (searchQuery.trim().length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      await performSearch(searchQuery);
    }, 500);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery, performSearch]);

  const handleResultClick = (result: SearchResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    setSearchQuery('');
    setShowResults(false);
    setExpanded(false);

    // If this is a PP result, call the PP-specific callback
    if (result.isPP && result.ppData && onPPSelect) {
      onPPSelect(result.ppData.id, lat, lng);
    } else {
      // Otherwise, call the location select callback
      onLocationSelect(lat, lng, result.display_name);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleToggle = () => {
    if (expanded) {
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
    }
    setExpanded(!expanded);
  };

  return (
    <>
      {/* Invisible overlay to catch clicks when search is open */}
      {(expanded || showResults) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1400, // Below search component but above everything else
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowResults(false);
            setExpanded(false);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      )}

      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'auto',
          zIndex: 1500, // Higher than map control buttons (1000) to create proper stacking context
        }}
      >
      {/* Expandable Search Input */}
      <TextField
        inputRef={inputRef}
        size="small"
        placeholder={placeholder || t('map.searchPlaceholder', 'Search location...')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()} // Prevent map drag/pan when interacting with search
        onFocus={() => {
          if (searchResults.length > 0) {
            setShowResults(true);
          }
        }}
        sx={{
          width: expanded ? 300 : 0,
          mr: expanded ? 1 : 0,
          transition: 'width 0.2s ease-in-out, margin-right 0.2s ease-in-out', // Smooth transition without delay
          overflow: 'hidden',
          '& .MuiInputBase-root': {
            transition: 'none', // Remove default MUI transitions
          },
        }}
        InputProps={{
          startAdornment: loading && (
            <InputAdornment position="start">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            '& fieldset': {
              borderColor: theme.palette.divider,
            },
          },
        }}
      />

      {/* Search Toggle Button */}
      <IconButton
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          color: 'text.primary',
          border: theme => `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
          },
          boxShadow: 2,
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        title={t('map.searchPlaceholder', 'Search location...')}
      >
        <SearchIcon />
      </IconButton>

      {/* Search Results Dropdown */}
      {expanded && showResults && searchResults.length > 0 && (
        <Paper
          elevation={8}
          onMouseDown={(e) => e.stopPropagation()} // Prevent map drag/pan
          onClick={(e) => e.stopPropagation()} // Prevent map click events
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0, // Align to right edge
            mt: 0.5,
            width: 350,
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 10, // Higher than siblings in this container
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            // Themed scrollbar
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(40, 40, 40, 0.5)' : 'rgba(240, 240, 240, 0.5)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(120, 120, 120, 0.8)' : 'rgba(160, 160, 160, 0.8)',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(140, 140, 140, 0.9)' : 'rgba(140, 140, 140, 0.9)',
              },
            },
            // Firefox scrollbar
            scrollbarWidth: 'thin',
            scrollbarColor: theme.palette.mode === 'dark'
              ? 'rgba(120, 120, 120, 0.8) rgba(40, 40, 40, 0.5)'
              : 'rgba(160, 160, 160, 0.8) rgba(240, 240, 240, 0.5)',
          }}
        >
          <List dense>
            {searchResults.map((result) => (
              <ListItem
                key={result.place_id}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleResultClick(result);
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.5)' : 'rgba(0, 0, 0, 0.04)',
                  },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  gap: 1,
                }}
              >
                {/* Icon to distinguish PP from location */}
                <Box sx={{
                  color: result.isPP ? '#1d388d' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {result.isPP ? (
                    <FactoryIcon fontSize="small" />
                  ) : (
                    <LocationIcon fontSize="small" />
                  )}
                </Box>

                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: result.isPP ? 600 : 500,
                        color: result.isPP ? '#1d388d' : 'text.primary',
                      }}
                    >
                      {result.isPP
                        ? result.ppData?.name
                        : result.display_name.split(',')[0]
                      }
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {result.isPP
                        ? `${t('cpe.fields.companyCui')}: ${result.ppData?.company_cui || 'N/A'} • ${result.ppData?.plant_type.join(', ') || 'N/A'}`
                        : result.display_name
                      }
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* No results message */}
      {expanded && showResults && searchResults.length === 0 && !loading && searchQuery.length >= 3 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            mt: 0.5,
            p: 2,
            width: 350,
            zIndex: 1400,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('map.noSearchResults', 'No locations found')}
          </Typography>
        </Paper>
      )}
    </Box>
    </>
  );
};

